/* ============================================================================
   RequestForm — the full BRD intake form.
   A sectioned, stepped form used to create a new request or edit an existing
   draft. Supports "Save draft" (persists without submitting) and "Submit".
   Every lookup-driven control is populated from GET /api/lookups.
   ============================================================================ */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut, isApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useLookups, type RequestDetail } from './data';
import ChipMultiSelect from './ChipMultiSelect';

type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

interface PendingFile {
  file: File;
  filename: string;
  kind: 'image' | 'reference' | 'competitor';
  sizeBytes?: number;
  contentType?: string;
  status: UploadStatus;
  error?: string;
}

/** Read a File as base64 (strips the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}

interface FormState {
  title: string;
  priorityCode: string;
  neededBy: string;
  objectives: string[];
  description: string;
  cta: string;
  metrics: string[];
  targetAudience: string;
  audienceAge: string;
  audienceGender: string;
  audienceOther: string;
  contentCopy: string;
  collateralTypeCode: string;
  formats: string[];
  sizes: string;
  channels: string[];
  campaignTheme: string;
  preferredColors: string;
  preferredImagery: string;
  designStyleCode: string;
  notes: string;
}

const EMPTY: FormState = {
  title: '',
  priorityCode: 'medium',
  neededBy: '',
  objectives: [],
  description: '',
  cta: '',
  metrics: [],
  targetAudience: '',
  audienceAge: '',
  audienceGender: '',
  audienceOther: '',
  contentCopy: '',
  collateralTypeCode: '',
  formats: [],
  sizes: '',
  channels: [],
  campaignTheme: '',
  preferredColors: '',
  preferredImagery: '',
  designStyleCode: '',
  notes: '',
};

const STEPS = ['Overview', 'Audience & message', 'Deliverables', 'Creative direction', 'Attachments'];

export default function RequestForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editingId = id ? Number(id) : null;
  const { user } = useAuth();
  const { lookups } = useLookups();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [existingCount, setExistingCount] = useState(0);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(Boolean(editingId));
  const [error, setError] = useState<string | null>(null);
  const [touchedTitle, setTouchedTitle] = useState(false);

  // Load an existing draft when editing.
  useEffect(() => {
    if (!editingId) return;
    let active = true;
    apiGet<RequestDetail>(`/api/requests/${editingId}`)
      .then((d) => {
        if (!active) return;
        setForm({
          title: d.title ?? '',
          priorityCode: d.priorityCode ?? 'medium',
          neededBy: d.neededBy ?? '',
          objectives: d.objectives ?? [],
          description: d.description ?? '',
          cta: d.cta ?? '',
          metrics: d.metrics ?? [],
          targetAudience: d.targetAudience ?? '',
          audienceAge: d.audienceAge ?? '',
          audienceGender: d.audienceGender ?? '',
          audienceOther: d.audienceOther ?? '',
          contentCopy: d.contentCopy ?? '',
          collateralTypeCode: d.collateralTypeCode ?? '',
          formats: d.formats ?? [],
          sizes: d.sizes ?? '',
          channels: d.channels ?? [],
          campaignTheme: d.campaignTheme ?? '',
          preferredColors: d.preferredColors ?? '',
          preferredImagery: d.preferredImagery ?? '',
          designStyleCode: d.designStyleCode ?? '',
          notes: d.notes ?? '',
        });
        setExistingCount(d.attachments?.length ?? 0);
      })
      .catch((e) => {
        if (active) setError(isApiError(e) ? e.message : 'Could not load this request.');
      })
      .finally(() => {
        if (active) setLoadingExisting(false);
      });
    return () => {
      active = false;
    };
  }, [editingId]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const titleError = touchedTitle && !form.title.trim() ? 'A title is required.' : '';

  const payload = useMemo(
    () => ({
      title: form.title.trim(),
      priorityCode: form.priorityCode || 'medium',
      neededBy: form.neededBy || undefined,
      objectives: form.objectives,
      description: form.description.trim() || undefined,
      cta: form.cta.trim() || undefined,
      metrics: form.metrics,
      targetAudience: form.targetAudience.trim() || undefined,
      audienceAge: form.audienceAge.trim() || undefined,
      audienceGender: form.audienceGender.trim() || undefined,
      audienceOther: form.audienceOther.trim() || undefined,
      contentCopy: form.contentCopy.trim() || undefined,
      collateralTypeCode: form.collateralTypeCode || undefined,
      formats: form.formats,
      sizes: form.sizes.trim() || undefined,
      channels: form.channels,
      campaignTheme: form.campaignTheme.trim() || undefined,
      preferredColors: form.preferredColors.trim() || undefined,
      preferredImagery: form.preferredImagery.trim() || undefined,
      designStyleCode: form.designStyleCode || undefined,
      notes: form.notes.trim() || undefined,
    }),
    [form],
  );

  function addFiles(fileList: FileList | null, kind: PendingFile['kind']): void {
    if (!fileList) return;
    const next: PendingFile[] = Array.from(fileList).map((f) => ({
      file: f,
      filename: f.name,
      kind,
      sizeBytes: f.size,
      contentType: f.type || undefined,
      status: 'pending' as UploadStatus,
    }));
    setPending((p) => [...p, ...next]);
  }

  /** Upload each not-yet-uploaded pending file to the request's attachments endpoint. */
  async function uploadPending(requestId: number, files: PendingFile[]): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      if (files[i].status === 'done') continue;
      setPending((p) => p.map((x, j) => (j === i ? { ...x, status: 'uploading', error: undefined } : x)));
      try {
        const dataBase64 = await fileToBase64(files[i].file);
        await apiPost(`/api/requests/${requestId}/attachments`, {
          filename: files[i].filename,
          kind: files[i].kind,
          contentType: files[i].contentType,
          sizeBytes: files[i].sizeBytes,
          dataBase64,
        });
        setPending((p) => p.map((x, j) => (j === i ? { ...x, status: 'done' } : x)));
      } catch (e) {
        const msg = isApiError(e) ? e.message : 'Upload failed.';
        setPending((p) => p.map((x, j) => (j === i ? { ...x, status: 'error', error: msg } : x)));
        throw e;
      }
    }
  }

  async function save(submit: boolean): Promise<void> {
    setTouchedTitle(true);
    if (!form.title.trim()) {
      setStep(0);
      setError('A title is required before saving.');
      return;
    }
    if (submit && !form.description.trim()) {
      setStep(0);
      setError('A description is required to submit.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let requestId = editingId;
      if (editingId) {
        // Update the existing draft (submit clears the draft flag).
        await apiPut(`/api/requests/${editingId}`, { ...payload, isDraft: submit ? false : undefined });
      } else {
        const res = await apiPost<{ id: number }>('/api/requests', {
          ...payload,
          isDraft: !submit,
        });
        requestId = res.id;
      }
      // Upload any chosen files (real blob upload) now that we have a request id.
      // A failed upload surfaces an error but the request itself is already saved.
      if (requestId && pending.some((f) => f.status !== 'done')) {
        await uploadPending(requestId, pending);
      }
      if (requestId) navigate(`/requests/${requestId}`);
      else navigate('/requests');
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not save the request.');
    } finally {
      setSaving(false);
    }
  }

  if (loadingExisting) {
    return (
      <div className="container page">
        <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const isLast = step === STEPS.length - 1;

  return (
    <div className="container page">
      <div className="page-title-row">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/requests')}>
            ← Cancel
          </button>
          <h1 style={{ marginTop: 8, marginBottom: 4 }}>{editingId ? 'Edit request' : 'New creative request'}</h1>
          <p className="meta">
            Requesting as <strong>{user?.name}</strong>
            {user?.email ? ` · ${user.email}` : ''}
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {STEPS.map((label, i) => (
          <button
            key={label}
            type="button"
            className={`chip ${i === step ? 'chip-active' : ''}`}
            onClick={() => setStep(i)}
            style={{ cursor: 'pointer' }}
          >
            <span className="mono" style={{ opacity: 0.7 }}>{i + 1}</span> {label}
          </button>
        ))}
      </div>

      <div className="card card-pad-lg">
        {step === 0 && (
          <>
            <div className={`field ${titleError ? 'field-error' : ''}`}>
              <label>Request title *</label>
              <input
                className="in"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                onBlur={() => setTouchedTitle(true)}
                placeholder="e.g. Cardiology open-house event poster"
              />
              {titleError && <span className="error-text">{titleError}</span>}
            </div>

            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="field">
                <label>Priority</label>
                <select className="in" value={form.priorityCode} onChange={(e) => set('priorityCode', e.target.value)}>
                  {lookups.priorities.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Desired completion date</label>
                <input type="date" className="in" value={form.neededBy} onChange={(e) => set('neededBy', e.target.value)} />
              </div>
            </div>

            <ChipMultiSelect
              label="Business objectives"
              hint="Select all that apply."
              options={lookups.objectives}
              selected={form.objectives}
              onChange={(v) => set('objectives', v)}
            />

            <div className="field">
              <label>Description / brief *</label>
              <span className="hint">What do you need, and what problem does it solve?</span>
              <textarea
                className="in"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Describe the request…"
              />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="field">
              <label>Call to action (CTA)</label>
              <input
                className="in"
                value={form.cta}
                onChange={(e) => set('cta', e.target.value)}
                placeholder="e.g. Register today, Call to book, Learn more"
              />
            </div>

            <ChipMultiSelect
              label="Success metrics"
              hint="How will success be measured?"
              options={lookups.metrics}
              selected={form.metrics}
              onChange={(v) => set('metrics', v)}
            />

            <div className="field">
              <label>Target audience</label>
              <input
                className="in"
                value={form.targetAudience}
                onChange={(e) => set('targetAudience', e.target.value)}
                placeholder="e.g. Expectant parents in Charlottesville"
              />
            </div>

            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div className="field">
                <label>Audience age <span className="muted">(optional)</span></label>
                <input className="in" value={form.audienceAge} onChange={(e) => set('audienceAge', e.target.value)} placeholder="e.g. 25–40" />
              </div>
              <div className="field">
                <label>Audience gender <span className="muted">(optional)</span></label>
                <input className="in" value={form.audienceGender} onChange={(e) => set('audienceGender', e.target.value)} placeholder="e.g. All" />
              </div>
              <div className="field">
                <label>Other audience notes <span className="muted">(optional)</span></label>
                <input className="in" value={form.audienceOther} onChange={(e) => set('audienceOther', e.target.value)} placeholder="e.g. Spanish-speaking" />
              </div>
            </div>

            <div className="field">
              <label>Content / copy</label>
              <span className="hint">Any provided text, headlines, or body copy.</span>
              <textarea className="in" value={form.contentCopy} onChange={(e) => set('contentCopy', e.target.value)} />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="field">
                <label>Collateral type</label>
                <select className="in" value={form.collateralTypeCode} onChange={(e) => set('collateralTypeCode', e.target.value)}>
                  <option value="">Select…</option>
                  {lookups.collateralTypes.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Specific sizes</label>
                <input
                  className="in"
                  value={form.sizes}
                  onChange={(e) => set('sizes', e.target.value)}
                  placeholder="e.g. 24×36 in, 1080×1920 px"
                />
              </div>
            </div>

            <ChipMultiSelect
              label="Deliverable formats"
              options={lookups.formats}
              selected={form.formats}
              onChange={(v) => set('formats', v)}
            />

            <ChipMultiSelect
              label="Marketing channels"
              hint="Where will this run?"
              options={lookups.channels}
              selected={form.channels}
              onChange={(v) => set('channels', v)}
            />
          </>
        )}

        {step === 3 && (
          <>
            <div className="field">
              <label>Campaign theme</label>
              <input className="in" value={form.campaignTheme} onChange={(e) => set('campaignTheme', e.target.value)} placeholder="e.g. Compassionate, community-first care" />
            </div>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="field">
                <label>Preferred colors</label>
                <input className="in" value={form.preferredColors} onChange={(e) => set('preferredColors', e.target.value)} placeholder="e.g. Navy, orange, white" />
              </div>
              <div className="field">
                <label>Design style</label>
                <select className="in" value={form.designStyleCode} onChange={(e) => set('designStyleCode', e.target.value)}>
                  <option value="">Select…</option>
                  {lookups.designStyles.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Preferred imagery</label>
              <input className="in" value={form.preferredImagery} onChange={(e) => set('preferredImagery', e.target.value)} placeholder="e.g. Real patients, warm candid photography" />
            </div>
            <div className="field">
              <label>Additional notes</label>
              <textarea className="in" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <p className="meta" style={{ marginTop: 0 }}>
              Attach reference material — any file type (documents, images, PPT, PDF, ZIP, TXT…), up to
              50&nbsp;MB each. Files upload when you save or submit the request.
            </p>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              {(['image', 'reference', 'competitor'] as const).map((kind) => (
                <div className="field" key={kind}>
                  <label style={{ textTransform: 'capitalize' }}>{kind} files</label>
                  <input type="file" multiple className="in" style={{ padding: 8, height: 'auto' }} onChange={(e) => addFiles(e.target.files, kind)} />
                </div>
              ))}
            </div>

            {existingCount > 0 && (
              <p className="meta">{existingCount} file(s) already attached to this request.</p>
            )}

            {pending.length > 0 && (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="tbl tbl-compact">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Kind</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((f, i) => (
                      <tr key={`${f.filename}-${i}`}>
                        <td>{f.filename}</td>
                        <td style={{ textTransform: 'capitalize' }}>{f.kind}</td>
                        <td>
                          {f.status === 'done' ? (
                            <span className="pill pill-success">Uploaded</span>
                          ) : f.status === 'uploading' ? (
                            <span className="pill pill-info">Uploading…</span>
                          ) : f.status === 'error' ? (
                            <span className="pill pill-danger" title={f.error}>Failed</span>
                          ) : (
                            <span className="pill">Ready</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {f.status !== 'done' && f.status !== 'uploading' && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => setPending((p) => p.filter((_, j) => j !== i))}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <hr className="divider" />
            <h3 style={{ margin: '0 0 8px' }}>Ready to submit?</h3>
            <p className="meta" style={{ marginTop: 0 }}>
              Submitting sends this to the creative team as a <strong>New</strong> request. Saving a draft keeps it private to you.
            </p>
          </>
        )}

        {error && (
          <p className="error-text" style={{ marginTop: 8 }}>
            {error}
          </p>
        )}

        <hr className="divider" />
        <div className="row-between">
          <button className="btn btn-ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            ← Back
          </button>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-navy" disabled={saving} onClick={() => void save(false)}>
              {saving ? 'Saving…' : 'Save draft'}
            </button>
            {isLast ? (
              <button className="btn btn-primary" disabled={saving} onClick={() => void save(true)}>
                {saving ? 'Submitting…' : 'Submit request'}
              </button>
            ) : (
              <button className="btn btn-navy" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
